import { useMutation } from '@apollo/client';
import { Button, Card, Form } from 'antd';
import moment from 'moment-timezone';
import React, { useContext, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import GoBackButton from '../../../../../components/GoBackButton';
import Portal from '../../../../../components/Portal';
import { CREATE_USER } from '../../../graphql/Mutations';
import StaffForm from '../../sales/components/SalesTeamForm';
import ReplaceLibrarianModal from '../components/ReplaceLibrarianModal';
import {
  LIBRARY_ALREADY_HAS_LIBRARIAN,
  LIBRARY_STAFF_BASE_PATH,
  STAFF_ROLES,
} from '../staffConfig';

const CreateStaff = () => {
  const history = useHistory();
  const location = useLocation();
  const { returnPath = LIBRARY_STAFF_BASE_PATH, library } =
    location?.state || {};
  const [form] = Form.useForm();
  const { dispatch } = useContext(AppContext);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const [replacePrompt, setReplacePrompt] = useState(null);
  const [isReplaceLoading, setIsReplaceLoading] = useState(false);
  const lastPayloadRef = useRef(null);

  const [createUser] = useMutation(CREATE_USER, {
    context: { skipToastCodes: [LIBRARY_ALREADY_HAS_LIBRARIAN] },
    onError: (error) => {
      const graphQLError = error?.graphQLErrors?.[0];
      if (graphQLError?.extensions?.code === LIBRARY_ALREADY_HAS_LIBRARIAN) {
        setReplacePrompt({ message: graphQLError?.message });
      }
    },
  });

  const handleReplaceLibrarian = async () => {
    setIsReplaceLoading(true);
    const response = await createUser({
      variables: {
        data: { ...lastPayloadRef.current, replaceLibrarian: true },
      },
    });
    setIsReplaceLoading(false);
    if (response?.data) {
      setReplacePrompt(null);
      history?.replace(returnPath);
    }
  };

  const createStaffFun = async (staffDetails) => {
    setIsSubmitLoading(true);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: false,
    });
    lastPayloadRef.current = {
      firstName: staffDetails?.firstName?.trim(),
      lastName: staffDetails?.lastName?.trim(),
      gender: staffDetails?.gender,
      city: staffDetails?.city,
      state: staffDetails?.state,
      roles: [staffDetails?.staffRole],
      contactNo: staffDetails?.contact?.trim(),
      address: staffDetails?.address?.trim(),
      address2: staffDetails?.address2?.trim(),
      locality: staffDetails?.locality?.trim(),
      landmark: staffDetails?.landmark?.trim(),
      email: staffDetails?.email?.trim(),
      pincode: staffDetails?.pincode,
      staffLibraryId: staffDetails?.staffLibraryId,
      timezone: moment?.tz?.guess(),
    };
    const response = await createUser({
      variables: { data: lastPayloadRef.current },
    });
    if (response?.data) {
      history?.replace(returnPath);
    }
    setIsSubmitLoading(false);
  };

  const handleShowPrompt = () => {
    setIsDisabled(false);
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: true,
    });
  };

  const headerButtons = (
    <div className="header-btn-wrapper">
      <Button
        type="primary"
        className="common-button mr-16"
        size="middle"
        onClick={() => history?.push(returnPath)}
      >
        Cancel
      </Button>
      <Button
        className="common-button"
        size="middle"
        onClick={form.submit}
        loading={isSubmitLoading}
        disabled={isDisabled}
      >
        Save
      </Button>
    </div>
  );

  return (
    <>
      <ReplaceLibrarianModal
        open={!!replacePrompt}
        message={replacePrompt?.message}
        loading={isReplaceLoading}
        onConfirm={handleReplaceLibrarian}
        onCancel={() => setReplacePrompt(null)}
      />
      <Portal portalId="header-title-content">
        <GoBackButton customLink={returnPath} />
        <div className="portal-header">Add New Library Staff</div>
      </Portal>
      <Portal portalId="header-right-content">{headerButtons}</Portal>
      <Portal portalId="header-right-content-phones">{headerButtons}</Portal>
      <Card className="full-height-with-nav" title="Library Staff Details">
        <div className="delivery-form">
          <StaffForm
            form={form}
            onFinish={createStaffFun}
            handleShowPrompt={handleShowPrompt}
            showLibrary
            hideCountry
            roleOptions={STAFF_ROLES}
            initialValues={
              library ? { staffLibraryId: library?.id } : undefined
            }
            libraryData={library}
          />
        </div>
      </Card>
    </>
  );
};

export default CreateStaff;
